import {
  Application,
  ApplicationAction,
  ApplicationActionType,
  CatalogueItem,
} from 'src/features/common';
import { CartItem } from 'src/features/common/types/application/cart';
import { Collection } from 'src/features/common/types/application/collection';
import { AppDelivery } from 'src/features/common/types/application/delivery';
import { getProduct } from 'src/features/device-activation/helpers/utils';
import { DEVICE_NAMES } from 'src/features/order/tech-debt/product-details-extra';

export const updateDeliveryArrays = (
  deliveries: AppDelivery[],
  collections: Collection[],
  device: AppDelivery | Collection,
  isDelivery: boolean,
) => {
  const updatedDeliveries = deliveries.map((delivery) => {
    if (delivery.productId === device.productId && delivery.addressId === device.addressId && delivery.priceType === device.priceType) {
      return {
        ...delivery,
        quantity: isDelivery
          ? delivery.quantity + device.quantity
          : delivery.quantity - device.quantity,
      };
    }
    return delivery;
  });

  const updatedCollections = collections.map((collection) => {
    if (collection.productId === device.productId && collection.addressId === device.addressId && collection.priceType === device.priceType) {
      return {
        ...collection,
        quantity: isDelivery
          ? collection.quantity - device.quantity
          : collection.quantity + device.quantity,
      };
    }
    return collection;
  });

  // If the device doesn't exist in the deliveries array and it's a delivery, add it
  if (
    isDelivery &&
    !deliveries.some(
      (delivery) =>
        delivery.productId === device.productId && delivery.addressId === device.addressId && delivery.priceType === device.priceType,
    )
  ) {
    updatedDeliveries.push({ ...device, quantity: device.quantity } as AppDelivery);
  }

  // If the device doesn't exist in the collections array and it's not a delivery, add it
  if (
    !isDelivery &&
    !collections.some(
      (collection) =>
        collection.productId === device.productId && collection.addressId === device.addressId && collection.priceType === device.priceType,
    )
  ) {
    updatedCollections.push({ ...device, quantity: device.quantity });
  }

  // Return updated arrays deliveries and collections. Exclude items where quantity is 0 or less
  return {
    deliveries: updatedDeliveries.filter((delivery) => delivery.quantity > 0),
    collections: updatedCollections.filter((collection) => collection.quantity > 0),
  };
};

export const updateDeliveryArraysV2 = (
  deliveries: AppDelivery[],
  collections: Collection[],
  device: AppDelivery | Collection,
  isDelivery: boolean,
) => {
  const deliveriesToUpdate = deliveries.filter(
    (delivery) => delivery.productId === device.productId,
  );
  const collectionsToUpdate = collections.filter(
    (collections) => collections.productId === collections.productId,
  );

  const updatedDeliveries = deliveries.map((delivery) => {
    if (
      deliveriesToUpdate.map((delivery) => delivery.addressId).includes(delivery.addressId) &&
      delivery.productId === device.productId
    ) {
      return {
        ...delivery,
        quantity: isDelivery
          ? delivery.quantity + device.quantity
          : delivery.quantity - delivery.quantity,
      };
    }
    return delivery;
  });

  const updatedCollections = collections.map((collection) => {
    if (
      collectionsToUpdate.map((delivery) => delivery.addressId).includes(collection.addressId) &&
      collection.productId === device.productId
    ) {
      return {
        ...collection,
        quantity: isDelivery
          ? collection.quantity - collection.quantity
          : collection.quantity + device.quantity,
      };
    }
    return collection;
  });

  // If the device(s) don't exist in the deliveries array and it's a delivery, add it
  if (isDelivery && !deliveries.some((delivery) => delivery.productId === device.productId)) {
    collectionsToUpdate.forEach((collection) => {
      updatedDeliveries.push({ ...collection, quantity: collection.quantity } as AppDelivery);
    });
  }

  // If the device(s) don't exist in the collections array and it's not a delivery, add it
  if (!isDelivery && !collections.some((collection) => collection.productId === device.productId)) {
    deliveriesToUpdate.forEach((delivery) => {
      updatedCollections.push({ ...delivery, quantity: delivery.quantity });
    });
  }

  // Return updated arrays deliveries and collections. Exclude items where quantity is 0 or less
  return {
    deliveries: updatedDeliveries.filter((delivery) => delivery.quantity > 0),
    collections: updatedCollections.filter((collection) => collection.quantity > 0),
  };
};

export const addSerialNumber = (
  serialNumber: string,
  products: CatalogueItem[],
  application: Application,
  onDuplicate: (hasDuplicate: boolean) => void,
): ApplicationAction[] | undefined => {
  const product = getProduct(products, serialNumber.toUpperCase());
  const isDuplicate = application.cardMachines?.find((cardMachine) =>
    cardMachine.barcode.toLowerCase().includes(serialNumber.toLowerCase()),
  );

  if (!!isDuplicate) {
    onDuplicate(true);
    return;
  }

  return [
    {
      type: ApplicationActionType.UPDATE_CARD_MACHINE,
      cardMachines: [
        ...(application.cardMachines || []),
        {
          barcode: serialNumber.toUpperCase(),
          name: '',
          productId: product.device.id,
          acquiringChannel: product.device.acquiringChannel,
        },
      ],
    },
  ];
};

export function isInCartAndCanBeCollected(
  deviceName: string,
  cartItems: CartItem[],
  collections: Collection[],
) {
  const device = cartItems.find((item) => item.title === deviceName);

  if (!device) return false;

  return !!collections.find((collect) => collect.productId === device.productId);
}

export const validateSerialNumber = (
  serialNumber: string,
  application: Application,
): string | undefined => {
  const prefix = serialNumber.startsWith('N5')
    ? serialNumber.slice(0, 2).toUpperCase()
    : serialNumber.slice(0, 3).toUpperCase();
  let isCollectable: boolean = true;
  let scannedMessage: string | undefined = '';
  let noCollectionMessage: string = '';

  switch (prefix) {
    case 'K3R':
      isCollectable = isInCartAndCanBeCollected(
        DEVICE_NAMES.VODAPAY_KWIKA,
        application.cart || [],
        application.collections || [],
      );
      noCollectionMessage = 'VodaPay Kwika is not marked as collect';
      break;
    case 'N86':
      isCollectable = isInCartAndCanBeCollected(
        DEVICE_NAMES.MAX_2,
        application.cart || [],
        application.collections || [],
      );
      noCollectionMessage = 'VodaPay MAX 2 is not marked as collect';
      break;
    case 'N5':
      isCollectable = isInCartAndCanBeCollected(
        DEVICE_NAMES.VODAPAY_MAX,
        application.cart || [],
        application.collections || [],
      );
      noCollectionMessage = 'VodaPay MAX is not marked as collect';
      break;
    default:
      scannedMessage = undefined;
  }

  return isCollectable ? scannedMessage : noCollectionMessage;
};
